/**
 * Central brand registry.
 * Why this matters: every route, storage key, asset path, and content bucket
 * should come from one source of truth instead of being hardcoded per page.
 */

export type BrandKey =
  | 'uscitrusnursery'
  | 'uscitrus'
  | 'drmanismagic'
  | 'shoproyalgold';

export interface BrandConfig {
  key: BrandKey;
  domain: string;
  displayName: string;
  shortName: string;
  routeSegment: BrandKey;
  storagePrefix: string;
  logoSrc: string | null;
  siteUrl: string;
  quizProductBaseUrl: string;
  blogBaseUrl: string;
  contentPaths: {
    faqBasePath: string;
    oldBlogPostsCsvPath: string;
    markdownDirectory: string;
  };
  theme: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    text: string;
  };
}

export const DEFAULT_BRAND_KEY: BrandKey = 'uscitrusnursery';

export const BRAND_CONFIGS: Record<BrandKey, BrandConfig> = {
  uscitrusnursery: {
    key: 'uscitrusnursery',
    domain: 'uscitrusnursery.com',
    displayName: 'US Citrus Nursery',
    shortName: 'USCN',
    routeSegment: 'uscitrusnursery',
    storagePrefix: 'brand_uscitrusnursery',
    logoSrc: '/uscn-logo.png',
    siteUrl: 'https://uscitrusnursery.com',
    quizProductBaseUrl: 'https://uscitrusnursery.com/products',
    blogBaseUrl: 'https://uscitrusnursery.com/blogs/citrus-simplified',
    contentPaths: {
      faqBasePath: '/brands/uscitrusnursery/tree-faqs',
      oldBlogPostsCsvPath: '/brands/uscitrusnursery/old-blog-posts/USCNBlogPosts_Updated.csv',
      markdownDirectory: 'brands/uscitrusnursery/markdowns',
    },
    theme: {
      primary: '#2D5016',
      secondary: '#4CAF50',
      accent: '#FF6B35',
      background: '#F5F1EB',
      text: '#1a1a1a',
    },
  },
  uscitrus: {
    key: 'uscitrus',
    domain: 'uscitrus.com',
    displayName: 'US Citrus',
    shortName: 'US Citrus',
    routeSegment: 'uscitrus',
    storagePrefix: 'brand_uscitrus',
    logoSrc: null,
    siteUrl: 'https://uscitrus.com',
    quizProductBaseUrl: 'https://uscitrus.com/products',
    blogBaseUrl: 'https://uscitrus.com/blogs/citrus-simplified',
    contentPaths: {
      faqBasePath: '/brands/uscitrus/tree-faqs',
      oldBlogPostsCsvPath: '/brands/uscitrus/old-blog-posts/USCNBlogPosts_Updated.csv',
      markdownDirectory: 'brands/uscitrus/markdowns',
    },
    theme: {
      primary: '#1E4D8C',
      secondary: '#4F8BC9',
      accent: '#F59E0B',
      background: '#F5F9FF',
      text: '#132238',
    },
  },
  drmanismagic: {
    key: 'drmanismagic',
    domain: 'drmanismagic.com',
    displayName: "Dr. Mani's Magic",
    shortName: 'Dr. Mani',
    routeSegment: 'drmanismagic',
    storagePrefix: 'brand_drmanismagic',
    logoSrc: 'https://cdn.shopify.com/s/files/1/0573/0097/5798/files/drmm-logo.png?v=1786445986&height=40',
    siteUrl: 'https://drmanismagic.com',
    quizProductBaseUrl: 'https://drmanismagic.com/products',
    blogBaseUrl: 'https://drmanismagic.com/blogs/news',
    contentPaths: {
      faqBasePath: '/brands/drmanismagic/tree-faqs',
      oldBlogPostsCsvPath: '/brands/drmanismagic/old-blog-posts/USCNBlogPosts_Updated.csv',
      markdownDirectory: 'brands/drmanismagic/markdowns',
    },
    theme: {
      primary: '#4B2E83',
      secondary: '#7C5AC8',
      accent: '#F97316',
      background: '#F8F5FF',
      text: '#221633',
    },
  },
  shoproyalgold: {
    key: 'shoproyalgold',
    domain: 'shoproyalgold.com',
    displayName: 'Royal Gold',
    shortName: 'Royal Gold',
    routeSegment: 'shoproyalgold',
    storagePrefix: 'brand_shoproyalgold',
    logoSrc: null,
    siteUrl: 'https://shoproyalgold.com',
    quizProductBaseUrl: 'https://shoproyalgold.com/products',
    blogBaseUrl: 'https://shoproyalgold.com/blogs/news',
    contentPaths: {
      faqBasePath: '/brands/shoproyalgold/tree-faqs',
      oldBlogPostsCsvPath: '/brands/shoproyalgold/old-blog-posts/USCNBlogPosts_Updated.csv',
      markdownDirectory: 'brands/shoproyalgold/markdowns',
    },
    theme: {
      primary: '#4F3D1F',
      secondary: '#8B6B2E',
      accent: '#D4AF37',
      background: '#FCF8EF',
      text: '#241B10',
    },
  },
};

export const BRAND_ORDER: BrandKey[] = [
  'uscitrusnursery',
  'drmanismagic',
  'uscitrus',
  'shoproyalgold',
];

export const BRAND_OPTIONS = BRAND_ORDER.map((brandKey) => BRAND_CONFIGS[brandKey]);

export const isBrandKey = (value: string | undefined | null): value is BrandKey =>
  Boolean(value && value in BRAND_CONFIGS);

export const getBrandConfig = (brandKey?: string | null): BrandConfig =>
  isBrandKey(brandKey) ? BRAND_CONFIGS[brandKey] : BRAND_CONFIGS[DEFAULT_BRAND_KEY];
